import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import Slider from 'react-slick';

import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { ComparatorModel } from 'client/data/models/comparator';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { EditorialReviewEntities } from 'client/data/models/editorial-review';
import {
  getRatingScale,
  getOverallRating as getOverallEditorialRating,
} from 'site-modules/shared/utils/editorial-helper';
import { isUsed } from 'site-modules/shared/utils/inventory-utils/is-used';

// Components
import { ContentTable } from 'site-modules/shared/components/comparator/content-table/content-table';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { CarImage } from 'site-modules/shared/components/comparator/car-image/car-image';
import { CarName } from 'site-modules/shared/components/comparator/car-name/car-name';
import { OverallRating } from 'site-modules/shared/components/comparator/overall-rating/overall-rating';
import { EditorialReviewRating } from 'site-modules/shared/components/comparator/editorial-review-rating/editorial-review-rating';
import { EdmundsRatingButton } from 'site-modules/shared/components/comparator/buttons/edmunds-rating-button/edmunds-rating-button';
import { TypeBuyingGuideAd } from 'site-modules/shared/components/native-ad/type-buying-guide-ad/type-buying-guide-ad';

const SPONSORED_CELL_SEPARATOR_CLASS_NAME = 'sponsored-cell-separator';
const SPONSORED_CELL_CLASS_NAME = 'sponsored-cell px-0 pt-1 pb-0';

function ComparatorPreviewTableUI({ type, styles = [], columns = [], editorialReview = null, isMobile = false }) {
  const contentData = useMemo(() => {
    function getHeader(index) {
      const { year, makeName, modelName, name: styleName } = styles[index];
      return <Fragment>{`${year} ${makeName} ${modelName} ${styleName}`}</Fragment>;
    }

    function getCarName(index) {
      const { name } = columns[index];
      const { makeSlug, modelSlug, makeName, modelName, year, name: styleName, submodels } = styles[index];
      return (
        <div className="d-flex flex-column justify-content-between h-100 px-1 pt-1 px-md-0 pt-md-0_5">
          <CarName
            makeSlug={makeSlug}
            modelSlug={modelSlug}
            makeName={makeName}
            modelName={modelName}
            submodelSlug={submodels?.slug}
            year={year}
            styleName={styleName}
            columnName={name}
            titleClassName="heading-5 text-blue-50"
          />
        </div>
      );
    }

    function getPhoto(index) {
      const { makeSlug, modelSlug, submodels, trimName, trim, year } = styles[index];
      return (
        <AspectRatioContainer aspectRatio="3:2">
          <CarImage
            makeSlug={makeSlug}
            modelSlug={modelSlug}
            submodelName={submodels?.name}
            submodelSlug={submodels?.slug}
            trimName={trimName || trim}
            year={year}
            showPhotoflipperButton={false}
            imageClassName="m-0 w-100"
            imageWidth={'500'}
            lazy
          />
        </AspectRatioContainer>
      );
    }

    function getOverallRating(index) {
      return (
        <div className={classnames({ 'px-1': isMobile })}>
          <OverallRating
            rating={getOverallEditorialRating(editorialReview?.[index])}
            ratingScale={getRatingScale(get(editorialReview, `[${index}]`))}
            showStars={false}
            ratingScaleClassName={classnames({
              'heading-4 mt-1 mb-0_5': !isMobile,
              'heading-4': isMobile,
            })}
            showLogoDesign
            logoFillColor="#2070e8"
            ratingClassName="text-blue-50"
          />
        </div>
      );
    }

    function getEditorialReviewRating(index) {
      return (
        <div className={classnames({ 'px-1': isMobile })}>
          <EditorialReviewRating
            ratings={get(editorialReview, `[${index}].ratings`)}
            isScorecardV4={get(editorialReview, `[${index}].isScorecardV4`)}
            className="size-16"
          />
        </div>
      );
    }

    function getEdmundsRatingButton(index) {
      const { makeSlug, modelSlug, year, makeName, modelName } = styles[index];
      const { ctaUrl } = columns[index];

      return ctaUrl ? (
        <TypeBuyingGuideAd {...columns[index]} type={type} mobile={isMobile} />
      ) : (
        <div className={classnames('pt-0_5 pb-2_5', { 'mx-1': isMobile })}>
          <EdmundsRatingButton
            buttonColor="outline-blue-50"
            className={classnames('px-1_25 py-0_75 size-16', { 'w-100': isMobile })}
            makeSlug={makeSlug}
            modelSlug={modelSlug}
            year={year}
            toReviewPage={isUsed(get(styles, `[${index}].publicationState`, ''))}
            trackingId="mmy_select_submit_mmyt"
            makeName={makeName}
            modelName={modelName}
          />
        </div>
      );
    }

    function getEdmundsRatingCellClassName(index, name) {
      return columns[index].ctaText ? name : undefined;
    }

    const mobileRenderMap = columns.map((_, index) => [
      {
        isThead: true,
        features: [
          {
            srOnly: true,
            data: [getHeader(index)],
          },
        ],
      },
      {
        alternateFeaturesBgColor: false,
        features: [
          {
            rowClassName: 'car-action-row',
            data: [getCarName(index)],
          },
        ],
      },
      {
        alternateFeaturesBgColor: false,
        features: [
          {
            rowLabel: 'Photo',
            cellClassName: 'px-0',
            data: [getPhoto(index)],
          },
        ],
      },
      {
        alternateFeaturesBgColor: false,
        heading: 'Edmunds Rating',
        features: [
          {
            label: 'Overall Rating',
            data: [getOverallRating(index)],
          },
          {
            rowLabel: 'Rating breakdown',
            data: [getEditorialReviewRating(index)],
            cellClassName: getEdmundsRatingCellClassName(index, SPONSORED_CELL_SEPARATOR_CLASS_NAME),
          },
          {
            rowLabel: 'Full review',
            data: [getEdmundsRatingButton(index)],
            cellClassName: getEdmundsRatingCellClassName(index, SPONSORED_CELL_CLASS_NAME),
          },
        ],
      },
    ]);

    const desktopRenderMap = [
      {
        isThead: true,
        features: [
          {
            srOnly: true,
            data: columns.map((column, index) => getHeader(index)),
          },
        ],
      },
      {
        alternateFeaturesBgColor: false,
        features: [
          {
            rowClassName: 'car-action-row',
            data: columns.map(({ name }, index) => getCarName(index, name)),
          },
        ],
      },
      {
        alternateFeaturesBgColor: false,
        features: [
          {
            rowLabel: 'Photo',
            cellClassName: 'px-0',
            data: columns.map((column, index) => getPhoto(index)),
          },
        ],
      },
      {
        alternateFeaturesBgColor: false,
        heading: 'Edmunds Rating',
        features: [
          {
            label: 'Overall Rating',
            data: columns.map((column, index) => getOverallRating(index)),
          },
          {
            rowLabel: 'Rating breakdown',
            data: columns.map((column, index) => getEditorialReviewRating(index)),
            cellClassName: columns.map((column, index) =>
              getEdmundsRatingCellClassName(index, SPONSORED_CELL_SEPARATOR_CLASS_NAME)
            ),
          },
          {
            rowLabel: 'Full review',
            data: columns.map((column, index) => getEdmundsRatingButton(index)),
            cellClassName: columns.map((column, index) =>
              getEdmundsRatingCellClassName(index, SPONSORED_CELL_CLASS_NAME)
            ),
          },
        ],
      },
    ];

    return {
      mobileRenderMap,
      desktopRenderMap,
    };
  }, [columns, editorialReview, isMobile, styles, type]);

  function columnIndex(index) {
    return `column-${index}`;
  }

  function appendDots(dots) {
    return (
      <div>
        <ul className="slick-dots">{dots}</ul>
      </div>
    );
  }

  return (
    <div className="comparator-table">
      {isMobile ? (
        <Slider className="comparator-preview-slider" dots dotsClass="meatballs" appendDots={appendDots} arrows={false}>
          {contentData.mobileRenderMap.map((renderMap, index) => (
            <ContentTable
              key={columnIndex(index)}
              renderMap={renderMap}
              keyPrefix="features"
              isSmall={isMobile}
              noBorders={false}
              colNumber={1}
              tableClassName="mb-2"
              headingClassName="heading-4 mx-1"
              labelComponentClassName="size-16 px-1"
            />
          ))}
        </Slider>
      ) : (
        <ContentTable renderMap={contentData.desktopRenderMap} keyPrefix="features" noBorders={false} />
      )}
    </div>
  );
}

ComparatorPreviewTableUI.propTypes = {
  type: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      styleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      sponsoredLogo: PropTypes.string,
      ctaText: PropTypes.string,
      ctaUrl: PropTypes.string,
      displayUrl: PropTypes.string,
      clickOut: PropTypes.bool,
      showAdLabel: PropTypes.bool,
    })
  ).isRequired,
  styles: PropTypes.arrayOf(VehicleEntities.Style),
  editorialReview: PropTypes.arrayOf(EditorialReviewEntities.EditorialReview),
  isMobile: PropTypes.bool,
};

export const ComparatorPreviewTable = connectToModel(ComparatorPreviewTableUI, {
  editorialReview: bindToPath(
    ({ columns }) => `editorialReview["${columns.map(({ styleId }) => styleId)}"]`,
    ComparatorModel
  ),
});
